// @file Surface group action store

import { trackEvent } from '@@/bits/analytics'
import { captureMessage } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import { buildPresentationLink, getPresentationSearchParams, PresentationPageType } from '@@/bits/presentation'
import { SnackbarNotificationType, WallWishGroupByTypes, WallWishGroupByValue } from '@@/enums'
import type { OzMenuRowsRow } from '@@/library/v4/components/OzMenuRows.vue'
import { OzMenuRowsRowStyle } from '@@/library/v4/components/OzMenuRows.types'
import { navigateToUrlBridged } from '@@/native_bridge/bridge_url'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostGroupByStore } from '@@/pinia/surface_post_group_by_store'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { Id, PostAttributes, PostColor, WallGroupPost } from '@@/types'
import { useCopyToClipboard } from '@@/vuecomposables/copy_to_clipboard'
import { useScrollToSection } from '@@/vuecomposables/surface_scroll_to_section'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

enum SectionActionType {
  AddPost = 'AddPost',
  StartSlideshowFromThisSection = 'StartSlideshowFromThisSection',
  StartFieldTripFromThisSection = 'StartFieldTripFromThisSection',
  AddNewSectionBefore = 'AddNewSectionBefore',
  AddNewSectionAfter = 'AddNewSectionAfter',
  MoveSectionBefore = 'MoveSectionBefore',
  MoveSectionAfter = 'MoveSectionAfter',
  MakeSectionDefault = 'MakeSectionDefault',
  CopySectionName = 'CopySectionName',
  CopySectionBreakoutLink = 'CopySectionBreakoutLink',
  CopySubmissionRequestLink = 'CopySubmissionRequestLink',
  RenameSection = 'RenameSection',
  DeleteSection = 'DeleteSection',
}

type SurfaceSectionActionMenuItem = OzMenuRowsRow<SectionActionType> & {
  shouldShow: boolean
}

const useSurfaceGroupActionStore = defineStore('surfaceGroupActionStore', () => {
  const surfaceStore = useSurfaceStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceMapStore = useSurfaceMapStore()
  const surfaceShareLinksStore = useSurfaceShareLinksStore()
  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const surfacePostGroupByStore = useSurfacePostGroupByStore()
  const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
  const surfacePostsStore = useSurfacePostsStore()

  const { scrollToSection } = useScrollToSection()
  const { copyToClipboard } = useCopyToClipboard()

  // Getters

  const isSectionDefault = computed(() => {
    if (isAppUsing('groupPostsV2')) return surfacePostGroupByStore.isGroupUnderActionDefault
    return surfaceSectionsStore.sectionUnderActionId === surfaceSectionsStore.defaultSectionId
  })
  const isFirstSection = computed(() => {
    if (isAppUsing('groupPostsV2')) return surfacePostGroupByStore.isGroupUnderActionFirstGroup
    return surfaceSectionsStore.sectionUnderActionId === surfaceSectionsStore.sortedSectionIds[0]
  })
  const isLastSection = computed(() => {
    if (isAppUsing('groupPostsV2')) return surfacePostGroupByStore.isGroupUnderActionLastGroup
    return (
      surfaceSectionsStore.sectionUnderActionId ===
      surfaceSectionsStore.sortedSectionIds[surfaceSectionsStore.sortedSectionIds.length - 1]
    )
  })

  const isNoValueGroupWithRequiredProperty = computed(() => {
    return (
      surfacePostGroupByStore.currentSingleSelectProperty?.required === true &&
      surfacePostGroupByStore.groupUnderActionId === WallWishGroupByValue.NoValue
    )
  })

  const actionListGroups = computed(() => {
    const group1: SurfaceSectionActionMenuItem[] = [
      {
        icon: 'plus_thick',
        text: __('Add post'),
        eventPayload: SectionActionType.AddPost,
        shouldShow:
          surfacePermissionsStore.canIPost && !surfaceStore.isFrozen && !isNoValueGroupWithRequiredProperty.value,
      },
      {
        icon: 'play_outline',
        text: __('Start slideshow from this section'),
        eventPayload: SectionActionType.StartSlideshowFromThisSection,
        shouldShow: surfaceWishArrangementStore.isGroupBySection && !surfaceShareLinksStore.isBreakout,
        ariaDescription: __('Start the slideshow from this section'),
        testId: 'startSlideshowFromThisSection',
      },
      {
        icon: '360_view',
        text: __('Start virtual tour from this section'),
        eventPayload: SectionActionType.StartFieldTripFromThisSection,
        shouldShow:
          surfaceWishArrangementStore.isGroupBySection &&
          isAppUsing('virtualFieldTrip') &&
          surfaceStore.isMap &&
          !surfaceShareLinksStore.isBreakout,
        ariaDescription: __('Start the virtual tour from this section'),
        testId: 'startFieldTripFromThisSection',
      },
    ].filter((item) => item.shouldShow)

    const areSectionsHorizontal =
      surfaceStore.isGrid ||
      surfaceStore.isShelf ||
      (surfaceStore.isMap && surfaceMapStore.postListPosition === 'bottom') ||
      surfaceStore.isTimelineV1
    const directionalNewSectionLeftText = surfaceStore.dir === 'ltr' ? __('New section left') : __('New section right')
    const directionalNewSectionRightText = surfaceStore.dir === 'ltr' ? __('New section right') : __('New section left')
    const group2: SurfaceSectionActionMenuItem[] = [
      {
        icon: areSectionsHorizontal ? 'section_arrow_left' : 'section_arrow_up',
        text: areSectionsHorizontal ? directionalNewSectionLeftText : __('New section above'),
        eventPayload: SectionActionType.AddNewSectionBefore,
        shouldShow:
          surfacePermissionsStore.canIModerate && !surfaceStore.isFrozen && !surfaceWishArrangementStore.isGroupByColor,
        ariaDescription: __('Create a new section before the current section'),
      },
      {
        icon: areSectionsHorizontal ? 'section_arrow_right' : 'section_arrow_down',
        text: areSectionsHorizontal ? directionalNewSectionRightText : __('New section below'),
        eventPayload: SectionActionType.AddNewSectionAfter,
        shouldShow:
          surfacePermissionsStore.canIModerate &&
          !surfaceStore.isFrozen &&
          !surfaceWishArrangementStore.isGroupByColor &&
          !surfacePostGroupByStore.isGroupUnderActionIsNoValueGroup,
        ariaDescription: __('Create a new section after the current section'),
      },
    ].filter((item) => item.shouldShow)

    const group3: SurfaceSectionActionMenuItem[] = [
      {
        icon: areSectionsHorizontal ? 'arrow_left' : 'arrow_up',
        text: areSectionsHorizontal ? __('Move section left') : __('Move section up'),
        eventPayload: SectionActionType.MoveSectionBefore,
        shouldShow:
          surfacePermissionsStore.canIModerate &&
          !isFirstSection.value &&
          !surfaceStore.isFrozen &&
          !surfaceWishArrangementStore.isGroupByColor &&
          !surfacePostGroupByStore.isGroupUnderActionIsNoValueGroup,
        ariaDescription: __('Move the current section to before the previous section'),
      },
      {
        icon: areSectionsHorizontal ? 'arrow_right' : 'arrow_down',
        text: areSectionsHorizontal ? __('Move section right') : __('Move section down'),
        eventPayload: SectionActionType.MoveSectionAfter,
        shouldShow:
          surfacePermissionsStore.canIModerate &&
          !isLastSection.value &&
          !surfaceStore.isFrozen &&
          !surfaceWishArrangementStore.isGroupByColor &&
          !surfacePostGroupByStore.isGroupUnderActionIsNoValueGroup,
        ariaDescription: __('Move the current section to after the next section'),
      },
    ].filter((item) => item.shouldShow)

    const group4: SurfaceSectionActionMenuItem[] = [
      {
        icon: 'pencil_outline',
        text: __('Rename section'),
        eventPayload: SectionActionType.RenameSection,
        shouldShow:
          surfacePermissionsStore.canIModerate &&
          !surfaceStore.isFrozen &&
          !surfaceWishArrangementStore.isGroupByColor &&
          !surfacePostGroupByStore.isGroupUnderActionIsNoValueGroup,
      },
      {
        icon: 'crown',
        text: __('Make section default'),
        eventPayload: SectionActionType.MakeSectionDefault,
        shouldShow:
          surfacePermissionsStore.canIModerate &&
          !isSectionDefault.value &&
          !surfaceStore.isFrozen &&
          !surfaceWishArrangementStore.isGroupByColor,
        ariaDescription: __('New posts will be added to this section'),
      },
      {
        icon: 'copy_outline',
        text: __('Copy section name'),
        eventPayload: SectionActionType.CopySectionName,
        shouldShow: !surfaceWishArrangementStore.isGroupByColor,
      },
      {
        icon: 'grid_link',
        text: __('Copy breakout room link'),
        eventPayload: SectionActionType.CopySectionBreakoutLink,
        shouldShow:
          surfacePermissionsStore.canIAdminister &&
          !surfacePostGroupByStore.isGroupUnderActionIsNoValueGroup &&
          ((surfaceWishArrangementStore.isGroupBySection && !!surfaceShareLinksStore.sectionBreakoutLinks) ||
            surfaceWishArrangementStore.isGroupBySingleSelect ||
            surfaceWishArrangementStore.isGroupByColor),
      },
      {
        icon: 'inbox_link',
        text: __('Copy submission form'),
        eventPayload: SectionActionType.CopySubmissionRequestLink,
        shouldShow:
          surfaceShareLinksStore.isSubmissionRequestEnabled &&
          surfacePermissionsStore.canIAdminister &&
          !surfacePostGroupByStore.isGroupUnderActionIsNoValueGroup,
      },
      {
        icon: 'delete',
        text: __('Delete section'),
        rowStyle: OzMenuRowsRowStyle.Danger,
        eventPayload: SectionActionType.DeleteSection,
        shouldShow:
          surfacePermissionsStore.canIModerate &&
          !surfaceStore.isFrozen &&
          !surfaceWishArrangementStore.isGroupByColor &&
          !surfacePostGroupByStore.isGroupUnderActionIsNoValueGroup,
        testId: 'deleteSection',
      },
    ].filter((item) => item.shouldShow)

    return [group1, group2, group3, group4].filter((group) => group.length > 0)
  })

  const actionListGroupsOnlyContainsAddPost = computed(() => {
    return (
      actionListGroups.value.length === 1 &&
      actionListGroups.value[0].length === 1 &&
      actionListGroups.value[0][0].eventPayload === SectionActionType.AddPost
    )
  })

  // Actions

  function cancel(): void {
    surfaceSectionsStore.sectionUnderActionId = null
    surfacePostGroupByStore.groupUnderActionId = null
  }

  function handleSelectedAction(_: Event, action: SectionActionType): void {
    const currentGroupUnderActionId = isAppUsing('groupPostsV2')
      ? surfacePostGroupByStore.groupUnderActionId
      : surfaceSectionsStore.sectionUnderActionId
    if (currentGroupUnderActionId == null) {
      cancel()
      const context = { actionType: action }
      captureMessage('No section under action to handle the selected action', { context })
      return
    }
    switch (action) {
      case SectionActionType.AddNewSectionBefore:
        if (isAppUsing('groupPostsV2')) {
          void surfacePostGroupByStore.createGroupBefore(currentGroupUnderActionId as number)
        } else {
          surfaceSectionsStore.createSectionBefore(currentGroupUnderActionId as number)
        }
        break
      case SectionActionType.AddNewSectionAfter:
        if (isAppUsing('groupPostsV2')) {
          void surfacePostGroupByStore.createGroupAfter(currentGroupUnderActionId as number)
        } else {
          surfaceSectionsStore.createSectionAfter(currentGroupUnderActionId as number)
        }
        break
      case SectionActionType.AddPost: {
        const payload: PostAttributes = {}
        switch (surfaceWishArrangementStore.wishGroupBy) {
          case WallWishGroupByTypes.Section:
            payload.wall_section_id = isAppUsing('groupPostsV2')
              ? (surfacePostGroupByStore.groupUnderActionId as number)
              : (surfaceSectionsStore.sectionUnderActionId ?? undefined)
            break
          case WallWishGroupByTypes.Color:
            // We store default color as null in the backend
            payload.color =
              surfacePostGroupByStore.groupUnderActionId === 'default'
                ? undefined
                : (surfacePostGroupByStore.groupUnderActionId as PostColor)
            break
          default: {
            // We don't show add post button when group by author or none
            // This means we only go into this case when group by single select
            if (
              surfaceWishArrangementStore.customPropIdFromWishGroupBy != null &&
              surfacePostGroupByStore.groupUnderActionId != null &&
              surfacePostGroupByStore.groupUnderActionId !== WallWishGroupByValue.NoValue
            ) {
              payload.custom_properties = {
                [surfaceWishArrangementStore.customPropIdFromWishGroupBy]: surfacePostGroupByStore.groupUnderActionId,
              }
            }
            break
          }
        }
        if (surfaceStore.isMap) {
          const mapAttributes =
            payload.wall_section_id == null ? payload : { ...payload, sectionId: payload.wall_section_id }
          surfaceMapStore.startPickingLocation(mapAttributes)
        } else {
          void surfacePostsStore.startNewPost({
            attributes: payload,
          })
        }
        break
      }
      case SectionActionType.StartSlideshowFromThisSection: {
        const showLink = surfaceStore.links.show
        if (showLink != null && showLink !== '') {
          trackEvent('Surface', 'Opened slideshow from section')
          navigateToUrlBridged(
            buildPresentationLink({
              showLink,
              page: { pageType: PresentationPageType.Section, sectionId: currentGroupUnderActionId },
              searchParams: getPresentationSearchParams(),
              mode: 'slideshow',
            }),
          )
        }
        break
      }
      case SectionActionType.StartFieldTripFromThisSection: {
        const showLink = surfaceStore.links.show
        if (showLink != null && showLink !== '') {
          trackEvent('Surface', 'Opened virtual tour from section')
          navigateTo(
            buildPresentationLink({
              showLink,
              page: { pageType: PresentationPageType.Section, sectionId: currentGroupUnderActionId },
              searchParams: getPresentationSearchParams(),
              mode: 'field-trip',
            }),
            { target: '_blank' },
          )
        }
        break
      }
      case SectionActionType.MoveSectionBefore:
        if (surfaceWishArrangementStore.isGroupBySingleSelect) {
          if (surfacePostGroupByStore.currentSingleSelectProperty == null) break
          surfacePostPropertiesStore.moveSingleSelectPropertyOptionUp({
            propertyId: surfacePostGroupByStore.currentSingleSelectProperty.id,
            optionId: currentGroupUnderActionId as string,
          })
        } else {
          surfaceSectionsStore.moveSectionBefore(currentGroupUnderActionId as number)
          scrollToSection(currentGroupUnderActionId as number, { centralize: true, focusFirstButton: false })
        }
        break
      case SectionActionType.MoveSectionAfter:
        if (surfaceWishArrangementStore.isGroupBySingleSelect) {
          if (surfacePostGroupByStore.currentSingleSelectProperty == null) break
          surfacePostPropertiesStore.moveSingleSelectPropertyOptionDown({
            propertyId: surfacePostGroupByStore.currentSingleSelectProperty.id,
            optionId: currentGroupUnderActionId as string,
          })
        } else {
          surfaceSectionsStore.moveSectionAfter(currentGroupUnderActionId as number)
          scrollToSection(currentGroupUnderActionId as number, { centralize: true, focusFirstButton: false })
        }
        break
      case SectionActionType.CopySectionName:
        if (isAppUsing('groupPostsV2')) {
          if (surfacePostGroupByStore.groupUnderActionTitle != null) {
            void copyToClipboard({
              text: String(surfacePostGroupByStore.groupUnderActionTitle),
              globalSnackbarOptions: {
                notificationType: SnackbarNotificationType.success,
                message: __('Copied to clipboard'),
              },
            })
          }
        } else {
          if (surfaceSectionsStore.sectionUnderAction?.title) {
            void copyToClipboard({
              text: surfaceSectionsStore.sectionUnderAction.title,
              globalSnackbarOptions: {
                notificationType: SnackbarNotificationType.success,
                message: __('Copied to clipboard'),
              },
            })
          }
        }
        break
      case SectionActionType.CopySectionBreakoutLink:
        void surfaceShareLinksStore.copyGroupBreakoutLinkAndAlert(currentGroupUnderActionId)
        break
      case SectionActionType.CopySubmissionRequestLink:
        void surfaceShareLinksStore.copySubmissionRequestSectionLinkAndAlert(currentGroupUnderActionId)
        break
      case SectionActionType.RenameSection:
        if (isAppUsing('groupPostsV2')) {
          surfacePostGroupByStore.startRenamingGroup(currentGroupUnderActionId as number)
        } else {
          surfaceSectionsStore.startRenamingSection(currentGroupUnderActionId as number)
        }
        break
      case SectionActionType.DeleteSection:
        if (surfaceWishArrangementStore.isGroupBySingleSelect) {
          if (surfacePostGroupByStore.currentSingleSelectProperty == null) break
          void surfacePostPropertiesStore.onDeleteSingleSelectPropertyOption({
            propertyId: surfacePostGroupByStore.currentSingleSelectProperty.id,
            optionId: currentGroupUnderActionId as string,
          })
        } else {
          surfaceSectionsStore.askToDeleteSection(currentGroupUnderActionId as number)
        }
        break
      case SectionActionType.MakeSectionDefault:
        if (surfaceWishArrangementStore.isGroupBySingleSelect) {
          if (surfacePostGroupByStore.currentSingleSelectProperty == null) break
          surfacePostPropertiesStore.updateSingleSelectPropertyOptionAsDefault({
            propertyId: surfacePostGroupByStore.currentSingleSelectProperty.id,
            optionId: currentGroupUnderActionId as string,
          })
        } else {
          void surfaceSectionsStore.changeDefaultSection(currentGroupUnderActionId as number)
        }
        break
    }
    cancel()
  }

  /* ---------------------- */
  /* HOVERED SECTION        */
  /* ---------------------- */
  const hoveredSectionId = ref<WallGroupPost['id'] | null>(null)

  const setHoveredSectionId = (payload: { sectionId: WallGroupPost['id'] }): void => {
    if (hoveredSectionId.value === payload.sectionId) return
    hoveredSectionId.value = payload.sectionId
  }

  const clearHoveredSectionId = (payload: { sectionId: WallGroupPost['id'] }): void => {
    if (hoveredSectionId.value === payload.sectionId) {
      hoveredSectionId.value = null
    }
  }

  // Points the existing actionListGroups / handleSelectedAction machinery at a specific section by
  // writing its id into whichever "under action" ref the current groupPostsV2 mode reads from. Only
  // ever set transiently and paired with cancel() (or handleSelectedAction, which calls cancel()
  // itself), so the section action menu popover — which renders off these refs — never opens between
  // the set and reset within a single synchronous keypress.
  const pointActionsAtSection = (sectionId: WallGroupPost['id']): void => {
    if (isAppUsing('groupPostsV2')) {
      surfacePostGroupByStore.groupUnderActionId = sectionId
    } else {
      surfaceSectionsStore.sectionUnderActionId = sectionId as Id
    }
  }

  const isSectionActionShown = (action: SectionActionType): boolean =>
    actionListGroups.value.some((group) => group.some((item) => item.eventPayload === action))

  // Whether `action` would be offered for the currently hovered section — reuses the menu's own
  // shouldShow gating (permissions, frozen, first/last section, group-by mode, map-only actions).
  const canPerformOnHoveredSection = (action: SectionActionType): boolean => {
    if (hoveredSectionId.value == null) return false
    pointActionsAtSection(hoveredSectionId.value)
    const shown = isSectionActionShown(action)
    cancel()
    return shown
  }

  // Runs `action` against the hovered section by reusing the menu's own dispatch, so shortcuts stay
  // in lockstep with the menu — analytics, scroll-to-section, and the "can't delete the last section"
  // alert all come for free. handleSelectedAction calls cancel() when it's done.
  const performOnHoveredSection = (event: Event, action: SectionActionType): void => {
    if (hoveredSectionId.value == null) return
    pointActionsAtSection(hoveredSectionId.value)
    if (!isSectionActionShown(action)) {
      cancel()
      return
    }
    handleSelectedAction(event, action)
  }

  return {
    // Getters
    actionListGroups,
    actionListGroupsOnlyContainsAddPost,
    hoveredSectionId,

    // Actions
    cancel,
    handleSelectedAction,
    setHoveredSectionId,
    clearHoveredSectionId,
    canPerformOnHoveredSection,
    performOnHoveredSection,
  }
})
export { SectionActionType, useSurfaceGroupActionStore }
